import { useQuery } from '@tanstack/react-query';

import { describeStructure, priceGroupsQuery, rateStructuresQuery } from '../api/pricing.ts';
import { PageHeader } from '../components/page-header.tsx';

// The tenant's price groups and rate structures (CB-REQ PRC-01, PRC-02 at v1.942), read-only: a
// structure is immutable once created, and the product page is where a rate is set. Creating a new
// structure is an API capability today; its form arrives when a tenant needs one the starters do
// not cover.

export function PricingScreen() {
  const groups = useQuery(priceGroupsQuery);
  const structures = useQuery(rateStructuresQuery);
  const failure = groups.error ?? structures.error;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        kicker="Pricing"
        title="Price groups and rate structures"
        meta="Rates are ex GST and set per product, on each product’s page."
      />

      {failure ? (
        <p role="alert" className="alert">
          {failure.message}
        </p>
      ) : null}

      {groups.data ? (
        <section className="flex flex-col gap-3" aria-labelledby="groups">
          <h2 id="groups" className="section-title">
            Price groups
          </h2>
          <ul className="flex flex-wrap gap-2">
            {groups.data.map((g) => (
              <li key={g.id} className={g.isDefault ? 'tag tag-accent' : 'tag'}>
                {g.name}
                {g.isDefault ? ' · default' : ''}
              </li>
            ))}
          </ul>
          <p className="text-sm text-ink-soft">
            A product with no rate of its own in a group charges the default group’s rate.
          </p>
        </section>
      ) : null}

      {structures.data ? (
        <section className="flex flex-col gap-3" aria-labelledby="structures">
          <h2 id="structures" className="section-title">
            Rate structures
          </h2>
          <div className="overflow-x-auto">
            <table className="data-table">
              <caption className="sr-only">
                Rate structures and how each counts chargeable units
              </caption>
              <thead>
                <tr>
                  <th scope="col">Structure</th>
                  <th scope="col">Chargeable units</th>
                </tr>
              </thead>
              <tbody>
                {structures.data.map((s) => (
                  <tr key={s.id}>
                    <th scope="row" className="font-semibold">
                      {s.name}
                    </th>
                    <td className="text-sm text-ink-soft">{describeStructure(s)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
